import { useCallback, useEffect, useRef } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { SCROLL_TO_SIMILAR_ITEMS_EVENT } from "@/common/static/pdp.static";
import { SIMILAR_ROUTE } from "@/common/static/routes.static";
import {
  subscribe,
  unsubscribe,
} from "@/components/ProductDetail/utils/events";
import { ProductCard } from "@/features/search/components/product-card/ProductCard";
import { toCardProduct } from "@/features/search/utils/gateway-product.utils";
import useRelatedProducts from "@whoppah/apollo/hooks/useRelatedProducts";
import { Product } from "@whoppah/apollo/schema";
import { Button } from "@whoppah/ui/cva/Button";
import { Heading } from "@whoppah/ui/cva/Heading";

const NO_RELATED_PRODUCTS: Product[] = [];
const NUMBER_OF_RELATED_PRODUCTS = 12;

type ProductDetailsSimilarItemsProps = {
  product: ProductBySlugNonNullable;
  className?: string;
};

export const ProductDetailsSimilarItems = ({
  product,
  className,
}: ProductDetailsSimilarItemsProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { language } = useLanguage();

  const { loading, data } = useRelatedProducts({
    variables: {
      slug: product.slug,
      limit: NUMBER_OF_RELATED_PRODUCTS,
      lang: language,
    },
  });

  const products = data?.relatedProducts ?? NO_RELATED_PRODUCTS;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    subscribe(SCROLL_TO_SIMILAR_ITEMS_EVENT, () => {
      if (containerRef?.current) {
        containerRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    });

    return () => {
      unsubscribe(SCROLL_TO_SIMILAR_ITEMS_EVENT);
    };
  }, []);

  /** Track `view_item_list` event. */
  analyticsService.useExecAfterMount(
    useCallback(() => {
      if (!loading) {
        analyticsService.trackViewItemList(
          "pdp_similar_products",
          "pdp_similar_products",
          products
        );
      }
    }, [loading, products])
  );

  if (products.length === 0) {
    return null;
  }

  const handleProductCardClick = () => {
    /** Trigger `similar_item_pdp` event. */
    analyticsService.trackSimilarItemPdp();
  };

  /*
   * A button that navigates, not a link: `/similar` is robots-disallowed, yet a crawlable anchor
   * to it on every PDP got 1.47M such URLs discovered and 246k indexed (Search Console,
   * 2026-09-15).
   */
  const handleClick = () => {
    /** Triggers `show_more_similar_item_pdp` event. */
    analyticsService.trackShowMoreSimilarItemPdp();
    router.push(`${SIMILAR_ROUTE}/${product.slug}`);
  };

  return (
    <div ref={containerRef} className={className}>
      <div className="flex flex-col space-y-4">
        <Heading as="h4" className="mx-auto">
          {t("pdp:pdp-similar-items")}
        </Heading>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={toCardProduct(product)}
              onSelect={handleProductCardClick}
            />
          ))}
        </div>
        <Button
          type="button"
          onClick={handleClick}
          appearance="minimal"
          className="self-center"
        >
          {t("pdp:pdp-see-more-similar-items")}
        </Button>
      </div>
    </div>
  );
};
